import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Plus, Trash2, MessageSquare, Search, FileCode } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { BorderBeam } from '@/components/ui/border-beam'
import { DataTable } from '@/components/data/DataTable'
import { useToast } from '@/hooks/use-toast'
import { useAuthStore } from '@/app/store/auth'
import api from '@/lib/api/axios'
import { iacApi, type IaCProject } from '../api'
import { buildIconPath } from '@/lib/imagePaths'

interface IaCSession {
    sessionId: string
    name: string
    runbookRequestId: string
    mainId: string
    scenario: number
    scenarioName: string
    createdAt: string
    messageCount: number
    objectDescription: string
    hasProject: boolean
    projectId: number | null
    fileCount: number
    cloudPlatform: string | null
    projectDescription: string | null
    deploymentStatus: string | null
}

export function IaCListPage() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { toast } = useToast()
    const queryClient = useQueryClient()
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

    const [showNewDialog, setShowNewDialog] = useState(false)
    const [objectSearch, setObjectSearch] = useState('')
    const [selectedObject, setSelectedObject] = useState<string | null>(null)
    const [selectedScenario, setSelectedScenario] = useState<number>(1)
    const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)
    const [projectDescription, setProjectDescription] = useState('')
    const [showDescriptionStep, setShowDescriptionStep] = useState(false)

    // Fetch scenarios
    const { data: scenariosData } = useQuery({
        queryKey: ['recovery-scenarios'],
        queryFn: async () => {
            const res = await api.get('/api/recover-requests/filter-options/scenarios')
            return (res.data as any)?.data || []
        },
        enabled: isAuthenticated,
    })
    const scenarios = scenariosData || []

    // Fetch all IaC sessions and projects (shared across all users)
    const { data: sessionsData, isLoading } = useQuery({
        queryKey: ['iac-sessions'],
        queryFn: async () => {
            // Single consolidated query — the vw_IaCList SQL view assembles each row
            // (session + project + object description + deployment + scenario) server
            // side. Replaces the old client-side fan-out (iac-sessions + the heavy
            // /api/objects?limit=500 + a per-project deployment N+1 + a merge).
            const res = await iacApi.list()
            const rows = ((res.data as any)?.sessions || []) as any[]
            const results: IaCSession[] = rows.map((r) => ({
                sessionId: r.sessionId || '',
                name: (r.name || '').replace('Recovery Runbook ', '') || `IaC - ${r.mainId}`,
                runbookRequestId: r.runbookRequestId,
                mainId: r.mainId,
                scenario: r.scenario,
                scenarioName: r.scenarioName || `Scenario ${r.scenario}`,
                objectDescription: r.objectDescription || '',
                createdAt: r.createdAt,
                messageCount: r.messageCount || 0,
                hasProject: !!r.hasProject,
                projectId: r.projectId ?? null,
                fileCount: r.fileCount || 0,
                cloudPlatform: r.cloudPlatform ?? null,
                projectDescription: r.projectDescription ?? null,
                deploymentStatus: r.deploymentStatus ?? null,
            }))

            return results.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        },
        enabled: isAuthenticated,
    })

    // Fetch objects for the new project dialog
    const { data: objectsData } = useQuery({
        queryKey: ['objects-for-iac', objectSearch],
        queryFn: async () => {
            const res = await api.get('/api/objects', { params: { search: objectSearch || undefined, limit: 50 } })
            const d = res.data as any
            return d?.data || d?.items || []
        },
        enabled: isAuthenticated && showNewDialog,
    })

    const deleteMutation = useMutation({
        mutationFn: async (mainId: string) => {
            // Find and delete the chat session by looking up in the sessions list
            const session = sessions.find((s) => s.mainId === mainId)
            if (session?.sessionId) {
                await api.delete(`/api/ai/chat-history/session/${session.sessionId}`).catch(() => {})
            }
            // Delete the project if exists
            const projRes = await iacApi.listProjects(mainId)
            const projects = ((projRes.data as any)?.projects || []) as IaCProject[]
            for (const p of projects) {
                await iacApi.deleteProject(p.id).catch(() => {})
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['iac-sessions'] })
            setDeleteConfirm(null)
            toast({ title: 'IaC conversation deleted' })
        },
    })

    const sessions: IaCSession[] = sessionsData || []
    const objects = objectsData || []

    return (
        <div className="space-y-6" style={{ padding: 'var(--spacing-page, 24px)' }}>
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold">{t('navigation.iac', 'Infrastructure as Code')}</h1>
                    <p className="text-muted-foreground text-sm">{t('iac.description')}</p>
                </div>
                <Button onClick={() => setShowNewDialog(true)}>
                    <Plus className="h-4 w-4 mr-1.5" />New IaC Project
                </Button>
            </div>

            {/* Sessions list */}
            {isLoading ? (
                <div className="text-muted-foreground text-center py-12">{t('common.loading', 'Loading...')}</div>
            ) : sessions.length === 0 ? (
                <div className="text-center py-16 border rounded-lg">
                    <FileCode className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-30" />
                    <p className="text-muted-foreground">{t('iac.noProjectsYet')}</p>
                    <p className="text-xs text-muted-foreground mt-1">{t('iac.noProjectsHint')}</p>
                </div>
            ) : (
                <DataTable
                    columns={[
                        {
                            accessorKey: 'mainId',
                            header: 'Object',
                            cell: ({ row }: any) => (
                                <div>
                                    <div className="font-medium">{row.original.mainId}</div>
                                    <div className="text-xs text-muted-foreground">{row.original.objectDescription || ''}</div>
                                </div>
                            ),
                        },
                        {
                            accessorKey: 'scenarioName',
                            header: 'Scenario',
                            cell: ({ row }: any) => <span className="text-sm">{row.original.scenarioName}</span>,
                        },
                        {
                            accessorKey: 'createdAt',
                            header: 'Started',
                            cell: ({ row }: any) => (
                                <span className="text-muted-foreground">
                                    {new Date(row.original.createdAt).toLocaleDateString()} {new Date(row.original.createdAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                                </span>
                            ),
                        },
                        {
                            id: 'status',
                            header: 'Status',
                            cell: ({ row }: any) => {
                                const s = row.original as IaCSession
                                if (s.deploymentStatus === 'deployed') return <Badge variant="default" className="bg-green-600">Deployed</Badge>
                                if (s.deploymentStatus === 'destroyed') return <Badge variant="secondary">Destroyed</Badge>
                                if (s.cloudPlatform === 'vaulted' || s.deploymentStatus === 'vaulted') return <Badge variant="default" className="bg-blue-600">Vaulted</Badge>
                                if (s.hasProject && s.fileCount > 0) return <Badge variant="default">Generated</Badge>
                                if (s.hasProject) return <Badge variant="secondary">Saved</Badge>
                                if (s.messageCount > 2) return <Badge variant="outline">In Progress</Badge>
                                return <Badge variant="outline">Started</Badge>
                            },
                        },
                        {
                            accessorKey: 'projectDescription',
                            header: t('iac.descriptionColumn', 'Description'),
                            cell: ({ row }: any) => row.original.projectDescription ? (
                                <span className="text-sm text-muted-foreground">{row.original.projectDescription}</span>
                            ) : <span className="text-muted-foreground">-</span>,
                        },
                        {
                            id: 'actions',
                            header: '',
                            cell: ({ row }: any) => (
                                <div className="flex justify-end" onClick={(e: any) => e.stopPropagation()}>
                                    <Button variant="ghost" size="sm" className="h-7 text-xs text-destructive hover:text-destructive"
                                        onClick={() => setDeleteConfirm(row.original.mainId)}>
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                </div>
                            ),
                        },
                    ]}
                    data={sessions}
                    searchKey="mainId"
                    searchPlaceholder="Search projects..."
                    onRowClick={(row: any) => navigate(`/iac/${row.mainId}/${row.scenario}`)}
                />
            )}

            {/* New project dialog */}
            {showNewDialog && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div className="bg-card rounded-lg border p-6 shadow-lg w-full max-w-lg mx-4">
                        <h3 className="text-lg font-semibold mb-1">{t('iac.newIaCProject')}</h3>
                        <p className="text-sm text-muted-foreground mb-4">{t('iac.selectObjectAndScenario')}</p>

                        {/* Scenario selection */}
                        {scenarios.length > 0 && (
                            <div className="mb-3">
                                <label className="text-xs font-medium text-muted-foreground uppercase mb-1 block">{t('recoverRequests.scenario', 'Scenario')}</label>
                                <Select value={String(selectedScenario)} onValueChange={(val) => setSelectedScenario(Number(val))}>
                                    <BorderBeam active={true} duration={2} beamSize={180} strokeWidth={2} borderRadius={6} className="inline-block w-full">
                                        <SelectTrigger className="w-full text-left [&>span]:font-bold">
                                            <SelectValue placeholder={t('recoverRequests.scenario', 'Scenario')} />
                                        </SelectTrigger>
                                    </BorderBeam>
                                    <SelectContent>
                                        {scenarios.map((s: any) => (
                                            <SelectItem key={s.id} value={String(s.id)}>
                                                {s.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        {/* Object search */}
                        <div className="relative mb-3">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                value={objectSearch}
                                onChange={(e) => setObjectSearch(e.target.value)}
                                placeholder={t('iac.searchObjects')}
                                className="pl-9"
                            />
                        </div>

                        <div className="max-h-[300px] overflow-y-auto border rounded-lg">
                            {objects.length === 0 ? (
                                <div className="text-sm text-muted-foreground text-center py-8">{t('iac.noObjectsFound')}</div>
                            ) : (
                                objects.map((obj: any) => (
                                    <button
                                        key={obj.objectID}
                                        className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-accent transition-colors border-b last:border-b-0"
                                        onClick={() => {
                                            setSelectedObject(obj.objectID)
                                            setShowDescriptionStep(true)
                                        }}
                                    >
                                        <img src={buildIconPath(obj.iconFilename || 'vsiconblank.png')} alt="" className="h-6 w-6 object-contain flex-shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <div className="font-medium text-sm">{obj.objectID}</div>
                                            <div className="text-xs text-muted-foreground truncate">{obj.objectDescription || '-'}</div>
                                        </div>
                                    </button>
                                ))
                            )}
                        </div>

                        {/* Description step */}
                        {showDescriptionStep && selectedObject && (
                            <div className="mt-3 p-3 rounded-lg border bg-accent/30">
                                <p className="text-sm font-medium mb-1">{selectedObject}</p>
                                <p className="text-xs text-muted-foreground mb-2">{t('iac.descriptionPrompt', 'Enter a description for this project')}</p>
                                <Input
                                    value={projectDescription}
                                    onChange={(e) => setProjectDescription(e.target.value)}
                                    placeholder={t('iac.descriptionPlaceholder', 'e.g. AWS recovery with simplified VPC')}
                                    className="mb-3"
                                    autoFocus
                                    onKeyDown={async (e) => {
                                        if (e.key === 'Enter' && projectDescription.trim()) {
                                            try {
                                                const r = await iacApi.createProject({ mainId: selectedObject, name: `${selectedObject} - Terraform`, description: projectDescription.trim(), cloudPlatform: 'multi', scenario: selectedScenario })
                                                const newId = (r.data as any)?.project?.id
                                                setShowNewDialog(false)
                                                setShowDescriptionStep(false)
                                                setObjectSearch('')
                                                setProjectDescription('')
                                                navigate(`/iac/${selectedObject}/${selectedScenario}?pid=${newId}`)
                                            } catch { toast({ title: 'Failed to create project', variant: 'destructive' }) }
                                        }
                                    }}
                                />
                                <div className="flex justify-end gap-2">
                                    <Button variant="outline" size="sm" onClick={() => { setShowDescriptionStep(false); setSelectedObject(null); setProjectDescription('') }}>
                                        {t('iac.cancel')}
                                    </Button>
                                    <Button size="sm" disabled={!projectDescription.trim()} onClick={async () => {
                                        try {
                                            const r = await iacApi.createProject({ mainId: selectedObject, name: `${selectedObject} - Terraform`, description: projectDescription.trim(), cloudPlatform: 'multi', scenario: selectedScenario })
                                            const newId = (r.data as any)?.project?.id
                                            setShowNewDialog(false)
                                            setShowDescriptionStep(false)
                                            setObjectSearch('')
                                            setProjectDescription('')
                                            navigate(`/iac/${selectedObject}/${selectedScenario}?pid=${newId}`)
                                        } catch { toast({ title: 'Failed to create project', variant: 'destructive' }) }
                                    }}>
                                        {t('iac.createProject', 'Create Project')}
                                    </Button>
                                </div>
                            </div>
                        )}

                        {!showDescriptionStep && (
                            <div className="flex justify-end mt-4">
                                <Button variant="outline" size="sm" onClick={() => { setShowNewDialog(false); setObjectSearch(''); setSelectedObject(null) }}>{t('iac.cancel')}</Button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Delete confirmation */}
            {deleteConfirm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div className="bg-card rounded-lg border p-6 shadow-lg max-w-md mx-4">
                        <h3 className="text-lg font-semibold mb-2">Delete IaC Conversation?</h3>
                        <p className="text-sm text-muted-foreground mb-4">
                            This will delete the conversation, all generated files, and any deployment records for <strong>{deleteConfirm}</strong>. This cannot be undone.
                        </p>
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" size="sm" onClick={() => setDeleteConfirm(null)}>{t('iac.cancel')}</Button>
                            <Button variant="destructive" size="sm" onClick={() => deleteMutation.mutate(deleteConfirm)} disabled={deleteMutation.isPending}>
                                Delete
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
